import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";

const schema = z.object({
  signOffAction: z.string().min(1, "Select SignOff Action")
});

export type BulkSignOffValues = z.infer<typeof schema>;

interface BulkChangeSignOffStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: BulkSignOffValues) => void | Promise<void>;
  submitting?: boolean;
}

const SIGN_OFF_ACTIONS = ["Signed Off"];

export function BulkChangeSignOffStatusDialog({
  open,
  onClose,
  onSubmit,
  submitting = false
}: BulkChangeSignOffStatusDialogProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const form = useForm<BulkSignOffValues>({
    resolver: zodResolver(schema),
    defaultValues: { signOffAction: "" }
  });

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      form.reset({ signOffAction: "" });

      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });

      return;
    }

    previousFocusRef.current?.focus();
  }, [open, form]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-signoff-title"
        className="w-full max-w-lg rounded border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="bulk-signoff-title" className="text-base font-semibold text-slate-900">
            Bulk Sign-Off
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} ref={closeButtonRef} className="text-slate-500 hover:text-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={form.handleSubmit(async (values) => {
            await onSubmit(values);
          })}
          className="space-y-4 px-4 py-4"
        >
          <div>
            <label htmlFor="bulk-signoff-action" className="mb-1 block text-sm font-medium text-slate-700">
              Select Sign-Off Action <span className="text-red-600">*</span>
            </label>
            <select
              id="bulk-signoff-action"
              {...form.register("signOffAction")}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">--</option>
              {SIGN_OFF_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
            {form.formState.errors.signOffAction && (
              <p className="mt-1 text-xs text-red-600">{form.formState.errors.signOffAction.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
